import React, { useCallback, useMemo } from 'react';
import { RackStructure, BinLocation, BinLocationGroup } from '../rack-builder/types';
import { LocationHeatmapDto, HEAT_LEVEL_COLORS } from './types';
import { Activity, TrendingUp, Calendar } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { RackGridCore, CellColor } from '../../../common/rack-grid';

interface HeatmapRackGridProps {
    rackStructure: RackStructure;
    heatmapData: Map<string, LocationHeatmapDto>;
    /** Deep-link fra Reolbygger: lokasjon som skal highlightes og scrolles til */
    highlightBinCode?: string | null;
}

/**
 * HeatmapRackGrid — visningsmodus for aktivitetskartet.
 *
 * Fase 3-migrering: rendering delegeres til felles RackGridCore (samme motor som
 * Reolbygger), med heatfargene som farge-lag og aktivitetsstatistikk som tooltip.
 * Redigeringsmodusen (drag&drop) bruker fortsatt InteractiveRackBody — bevisst
 * ikke migrert (egen interaksjonsmodell, se Plan.md).
 */
export const HeatmapRackGrid: React.FC<HeatmapRackGridProps> = ({
    rackStructure,
    heatmapData,
    highlightBinCode
}) => {
    const { t } = useTranslation();

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('nb-NO', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    /** Heatfarge fra backend som farge-lag; hvit tekst på de mettede fargene */
    const cellColor = useCallback((location: BinLocation): CellColor | null => {
        const heatData = heatmapData.get(location.binCode);
        if (!heatData) {
            return { background: '#f3f4f6', border: '#d1d5db', text: '#9ca3af' };
        }
        return { background: heatData.heatColor, border: heatData.heatColor, text: '#ffffff' };
    }, [heatmapData]);

    /** Aktivitetsnivå + transaksjonsantall i cellen */
    const cellExtra = useCallback((group: BinLocationGroup): React.ReactNode => {
        const heatData = heatmapData.get(group.primaryLocation.binCode);
        if (!heatData) {
            return (
                <span className="text-gray-400">
                    {t('manager.modules.locationHeatmap.cell.noActivity', {}, 'Ingen aktivitet')}
                </span>
            );
        }
        return (
            <span className="font-semibold">
                {t('manager.modules.locationHeatmap.cell.level', { '0': heatData.heatLevel }, `Niv ${heatData.heatLevel}`)}
                {' · '}
                {t('manager.modules.locationHeatmap.cell.transShort', { '0': heatData.transactionCount }, `${heatData.transactionCount} trans.`)}
            </span>
        );
    }, [heatmapData, t]);

    /** Aktivitetsstatistikk-tooltip (portert fra den gamle grid-implementasjonen) */
    const renderTooltip = useCallback((location: BinLocation): React.ReactNode => {
        const heat = heatmapData.get(location.binCode);
        if (!heat) return null;
        return (
            <>
                <div className="font-bold mb-2 text-base">{heat.binCode}</div>
                <div className="space-y-1.5 text-gray-200">
                    <div className="flex items-center space-x-2">
                        <Activity className="w-4 h-4" />
                        <span className="font-semibold">{t('manager.modules.locationHeatmap.tooltip.activityLevel', { '0': heat.heatLevel }, `Aktivitetsnivå: ${heat.heatLevel}/5`)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <TrendingUp className="w-4 h-4" />
                        <span>{t('manager.modules.locationHeatmap.tooltip.transactions', { '0': heat.transactionCount }, `Transaksjoner: ${heat.transactionCount}`)}</span>
                    </div>
                    <div className="text-gray-300">
                        {t('manager.modules.locationHeatmap.tooltip.totalQuantity', { '0': heat.totalQuantity.toFixed(2) }, `Total mengde: ${heat.totalQuantity.toFixed(2)}`)}
                    </div>
                    <div className="text-gray-300">
                        {t('manager.modules.locationHeatmap.tooltip.activeDays', { '0': heat.uniqueDays, '1': heat.periodDays }, `Aktive dager: ${heat.uniqueDays} av ${heat.periodDays}`)}
                    </div>
                    {heat.avgTransPerDay && (
                        <div className="text-gray-300">
                            {t('manager.modules.locationHeatmap.tooltip.avgTransPerDay', { '0': heat.avgTransPerDay.toFixed(2) }, `Gj.snitt trans/dag: ${heat.avgTransPerDay.toFixed(2)}`)}
                        </div>
                    )}
                    <div className="border-t border-gray-700 pt-1.5 mt-1.5">
                        <div className="flex items-center space-x-2">
                            <Calendar className="w-3 h-3" />
                            <span className="text-gray-400 text-[10px]">
                                {formatDate(heat.firstTransaction)} → {formatDate(heat.lastTransaction)}
                            </span>
                        </div>
                    </div>
                    <div className="text-yellow-300 font-semibold mt-2">
                        {t('manager.modules.locationHeatmap.tooltip.percentile', { '0': heat.heatPercentile.toFixed(1) }, `Persentil: ${heat.heatPercentile.toFixed(1)}%`)}
                    </div>
                </div>
            </>
        );
    }, [heatmapData, t]);

    const allBinCodes = useMemo(
        () => rackStructure.locations.map(l => l.binCode),
        [rackStructure.locations]
    );

    const highlightSet = useMemo(
        () => (highlightBinCode ? new Set([highlightBinCode]) : undefined),
        [highlightBinCode]
    );

    if (rackStructure.locations.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                <p>{t('manager.modules.locationHeatmap.grid.noLocationsToShow', {}, 'Ingen lokasjoner å vise')}</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            <div className="flex-1 min-h-0">
                <RackGridCore
                    rackStructure={rackStructure}
                    selectedBinCodes={[]}
                    onSelectionChange={() => { /* ren visning — ingen seleksjon */ }}
                    filteredBinCodes={allBinCodes}
                    selectable={false}
                    showConfigInfo={false}
                    cellColor={cellColor}
                    cellExtra={cellExtra}
                    renderTooltip={renderTooltip}
                    highlightedBinCodes={highlightSet}
                    scrollToBinCode={highlightBinCode ?? undefined}
                    hideFooter
                />
            </div>

            {/* Legend — fargene hentes fra HEAT_LEVEL_COLORS som speiler backendens HeatColor-beregning */}
            <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs font-semibold text-gray-700 mb-2">{t('manager.modules.locationHeatmap.legend.title', {}, 'Aktivitetsnivå:')}</div>
                <div className="flex items-center flex-wrap gap-3 text-xs">
                    {([1, 2, 3, 4, 5] as const).map(level => (
                        <div key={level} className="flex items-center space-x-1.5">
                            <div className="w-6 h-6 rounded border border-gray-300" style={{ backgroundColor: HEAT_LEVEL_COLORS[level] }} />
                            <span>{t(`manager.modules.locationHeatmap.legend.level${level}`, {}, `Nivå ${level}`)}</span>
                        </div>
                    ))}
                    <div className="flex items-center space-x-1.5">
                        <div className="w-6 h-6 bg-gray-100 rounded border border-gray-300" />
                        <span>{t('manager.modules.locationHeatmap.cell.noActivity', {}, 'Ingen aktivitet')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
